// Parent credentials live in this tab only. Storage denial falls back to memory;
// nothing is written to cookies or localStorage. Secrets never enter API URLs.
const STORAGE_KEY = 'kk_parent_session_v1';
const parentPages = /^\/(student360|puanlarim|avatarim|odemelerim)(?:\/|$)|^\/mini-tasks\/parent(?:\/|$)/;
let current = null;
let handoff = null;
let exchangeFlight = null;
let refreshFlight = null;
let generation = 0;

export function isParentSessionRequest(base, url) {
  if (!parentPages.test(window.location.pathname)) return false;
  try {
    const target = new URL(url, window.location.origin);
    const api = new URL(base);
    if (target.origin !== api.origin || !target.pathname.startsWith(`${api.pathname.replace(/\/$/, '')}/`)) return false;
    const path = target.pathname.slice(api.pathname.replace(/\/$/, '').length);
    return /^\/(parent|mini-tasks)(?:\/|$)/.test(path)
      || /^\/auth\/(session|privacy-notice)(?:\/|$)/.test(path);
  } catch { return false; }
}

function save(value) {
  current = value;
  try {
    if (value) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* The open page works even when all browser storage is blocked. */ }
}

export function clearParentSession() { generation += 1; save(null); }

export function readParentSession(base) {
  if (!current) {
    try { current = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY)); } catch { /* memory only */ }
  }
  if (current && (current.apiBase !== base || typeof current.access_token !== 'string'
    || typeof current.refresh_token !== 'string' || !Number.isFinite(current.refreshUntil)
    || current.refreshUntil <= Date.now())) save(null);
  return current;
}

function accept(base, payload) {
  if (typeof payload?.access_token !== 'string' || !payload.access_token
    || typeof payload?.refresh_token !== 'string' || !payload.refresh_token
    || !Number.isFinite(payload.access_expires_in) || payload.access_expires_in <= 0
    || !Number.isFinite(payload.refresh_idle_in) || payload.refresh_idle_in <= 0
    || !Number.isFinite(payload.refresh_abs_in) || payload.refresh_abs_in <= 0) {
    throw new Error('Invalid parent session response');
  }
  save({ apiBase: base, access_token: payload.access_token, refresh_token: payload.refresh_token,
    accessUntil: Date.now() + payload.access_expires_in * 1000,
    refreshUntil: Date.now() + Math.min(payload.refresh_idle_in, payload.refresh_abs_in) * 1000 });
}

async function post(base, path, body) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  let response;
  try {
    response = await fetch(`${base}/auth/parent-session/${path}`, {
      method: 'POST', credentials: path === 'logout' ? 'include' : 'omit', cache: 'no-store', redirect: 'error', referrerPolicy: 'no-referrer',
      signal: controller.signal, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
  } finally { clearTimeout(timeout); }
  if (!response.ok) {
    const error = new Error('Parent session unavailable');
    error.status = response.status;
    throw error;
  }
  return response;
}

export async function renewParentSession(base) {
  if (refreshFlight) return refreshFlight;
  const session = readParentSession(base);
  if (!session) return false;
  refreshFlight = (async () => {
    try {
      const response = await post(base, 'refresh', { refresh_token: session.refresh_token });
      const payload = await response.json();
      // An old in-flight refresh cannot overwrite a newer login or logout.
      if (current === session) accept(base, payload);
      return current !== null;
    } catch (error) {
      if ((error.status === 401 || error.status === 403) && current === session) clearParentSession();
      throw error;
    } finally { refreshFlight = null; }
  })();
  return refreshFlight;
}

export async function ensureParentSession(base) {
  if (!parentPages.test(window.location.pathname)) return;
  const fragment = new URLSearchParams(window.location.hash.slice(1));
  if (fragment.has('parent_login')) {
    handoff = fragment.get('parent_login');
    // Remove before any API request, navigation, retry or rendered child page.
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    clearParentSession();
  }
  if (handoff !== null) {
    if (!/^[A-Za-z0-9_-]{43}$/.test(handoff)) {
      handoff = null;
      const error = new Error('Invalid login code'); error.status = 401; throw error;
    }
    if (!exchangeFlight) {
      const code = handoff;
      const startedGeneration = generation;
      exchangeFlight = (async () => {
        try {
          const response = await post(base, 'exchange', { code });
          const payload = await response.json();
          if (startedGeneration === generation && handoff === code) accept(base, payload);
          if (handoff === code) handoff = null;
        } catch (error) {
          if (error.status === 401 || error.status === 403) handoff = null;
          throw error;
        } finally { exchangeFlight = null; }
      })();
    }
    await exchangeFlight;
  }
  const session = readParentSession(base);
  if (session && session.accessUntil <= Date.now() + 5000) await renewParentSession(base);
}

export function parentRequestOptions(base, url, options) {
  if (!isParentSessionRequest(base, url)) return options;
  const session = readParentSession(base);
  if (!session) return options;
  const headers = new Headers(options.headers);
  // Explicit caller credentials retain their own scope (e.g. teacher PIN).
  if (headers.has('Authorization')) return options;
  headers.set('Authorization', `Bearer ${session.access_token}`);
  return { ...options, headers, credentials: 'omit', redirect: 'error' };
}

export async function logoutParentSession(base) {
  const session = readParentSession(base);
  clearParentSession();
  if (session) await post(base, 'logout', { refresh_token: session.refresh_token });
}
